import { useAtom } from "jotai";
import Peaks, { CreateSegmentLabelOptions, CreateSegmentMarkerOptions, PeaksInstance, PeaksOptions, Segment, SegmentAddOptions, SegmentDragEvent, SegmentMarker, SegmentMouseEvent } from "peaks.js";
import {useEffect, useRef, useState} from "react";
import ReactPlayer from "react-player";
import { fromEvent, throttleTime } from "rxjs";
import { subtitleJsonStr } from "../components/TimeLine/jsonTest";
import { captionListState, segmentsAtom } from "../states/caption";
import { captionSelectionModeState, currentActivatedSegmentAtom, reactPlayerAtom } from "../states/initialize";
import { mp3UrlAtom } from "../states/timeline";
import { CaptionListProps } from "../types/caption";
import {playerState} from "../states/player";
import Konva from 'konva';
import {CustomSegmentMarker} from './customSegmentMarker'
import {secondsToFormattedTime} from '../helper/util'
import {timeStringToSeconds} from '../helper/util'
import { MINIMUM_CAPTION_GAP } from "../env-var/env-var";
import { findNextSegment } from "./peaksUtils";

declare global {
  var peaksInstance: PeaksInstance | undefined
  var latestClickedTime : number | undefined
  var previousSegment : Segment | undefined
  var currentActivatedSegment : CaptionListProps | undefined
  var currentTime : number | undefined
  var latestSeekedTime : number | undefined
  var latestStartTime : number | undefined
  var latestEndTime : number | undefined
  var latestSegmentId : number | undefined
  var latestSegmentDuration : number | undefined
}

type subtitle = {
  caption: string
  seq: number
  time_end: string
  time_ini: string
}

export const ORANGE_COLOR = "#FF9800"
export const BLUE_COLOR =  '#0A87FF'
export const INACTIVE_COLOR = '#ffdde5'

export function usePeaksjs() {

    const [isAutoCaptionHighlight, setIsAutoCaptionHighlight] = useAtom(captionSelectionModeState)

    const [segments, setSegments] = useAtom(segmentsAtom)
    const [captionList, setCaptionList] = useAtom(captionListState);
    const [peaksInstance, setPeaksInstance] = useState<PeaksInstance >();
  
    const [currentActivatedSegment, setCurrentActivatedSegment] = useAtom(currentActivatedSegmentAtom)
    const currentActivatedSegmentRef = useRef<CaptionListProps>();
    const nextAfterActivatedSegmentRef = useRef<Segment>();

    const currentHoverSegmentIdRef = useRef<string>();
    const previouslyHoverSegmentIdRef = useRef<string>();
    const currentClickedSegmentIdRef = useRef<string>();

    const audioElementRef = useRef<HTMLAudioElement>(null);
    const peaksInstanceRef = useRef<PeaksInstance >();
    const latestClickedTime = useRef(new Date().getTime());
    const latestSegmentId = useRef("");
    const zoomviewWaveformRef = useRef(null);
    const [mp3Url, ] = useAtom(mp3UrlAtom)
    const [reactPlayerGlobal, setReactPlayerGlobal] = useAtom(reactPlayerAtom)
    const [{playOn, muteOn, currentTime}, setPlayerState] = useAtom(playerState);    

    useEffect(function onSegmentChange() {
        if(segments == null || peaksInstance == null ) return

        const peaksSegments = peaksInstance.segments.getSegments()
        const foundSegment = peaksSegments.find(peaksSegment => {
                                                  return segments.findIndex(segment => segment!.labelText! !== peaksSegment!.labelText!)
                                                          === -1
                                                          ? false
                                                          : true
                                                } )

        if(foundSegment) {
              const modifiedSeg = segments.find(segment =>segment.id === foundSegment.id)
              foundSegment.update({labelText : modifiedSeg?.labelText})
        }
    }, [segments]);

    function createSegmentMarker(options : CreateSegmentMarkerOptions) {
        const customOption = {
          ...options
        }
        return new CustomSegmentMarker(options);
    }

    function formatAxisTime(time: number) {
        return secondsToFormattedTime(time)
    }

    function disableSegmentLabel(options : CreateSegmentLabelOptions) {
      if (options.view === 'overview') {
        return null;
      }

      return new Konva.Text({
        text:       "",
        fontSize:   0,
        fontFamily: 'Calibri',
        fill:       'black'
      });
    }

    function createPeaksOptions(segmentsLocalArr: SegmentAddOptions[], captionArr: CaptionListProps[]) {

              const options : PeaksOptions  = {
                formatAxisTime: formatAxisTime,
                createSegmentLabel :disableSegmentLabel,
                createSegmentMarker : createSegmentMarker,
                scrollbar: {
                  container: document.getElementById('scrollbar-container')!,
                  color: '#0a87ff',
                  minWidth: 30
                },
                zoomview: {
                  playheadColor: 'rgba(255, 255, 255, 1)',
                  wheelMode: 'scroll',
                  container : zoomviewWaveformRef.current,
                  waveformColor:  'rgba(255, 0, 255, 1)',
                  fontFamily : "Noto Sans KR",
                },
                zoomLevels :  [256,512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, 131072],
                mediaElement : audioElementRef.current!,
                webAudio: {
                  audioContext: new AudioContext()
                },
                segmentOptions : {
                  markers : true ,
                  overlay : true,
                  overlayFontSize : 16,
                  overlayOpacity: 0.2,
                  overlayLabelColor: '#ffffff',
                  overlayBorderWidth: 0,
                  overlayOffset: 40,
                },
                segments : captionArr.map((segment, i) => {
                  return {
                    startTime : Number(segment.startTime) ,
                    endTime : Number(segment.endTime),
                    labelText : `#${i} ${((segment.endTime as number) - (segment.startTime as number)).toFixed(1)}s \n\n${segment.labelText}` ,
                    id: segment.id,
                    editable : true,
                    color : '#ffdde5',
                  }
                }),
          };
          return options
    }

    function getSubtitleFromJson(subtitleJsonStr : string, captionArr : CaptionListProps[]) {
          return captionArr.map((segment : subtitle, i :number) => {
                                    const startTime = timeStringToSeconds(segment.time_ini)
                                    const endTime = timeStringToSeconds(segment.time_end)

                                    const obj: SegmentAddOptions = {
                                        startTime : Number((startTime).toFixed(1)),
                                        endTime : Number( (endTime).toFixed(1)),
                                        labelText : segment.caption,
                                        id: String(i + 1) ,
                                        editable : true,
                                        
                                    }
                                    return obj
                              }) as SegmentAddOptions[]
    }

    useEffect(function initializePeaks() {

        if(audioElementRef.current == null
          || mp3Url.length === 0
          || captionList == null
          || captionList.length <= 1
          || peaksInstanceRef.current != null
          ) return

        const segmentsLocalArr = getSubtitleFromJson(subtitleJsonStr, captionList)

        Peaks.init(createPeaksOptions(segmentsLocalArr, captionList), function(err, peaksInstance) {

          peaksInstance?.views.getView('zoomview')?.setAmplitudeScale(2.5);

            if (err || peaksInstance == null) {
              console.error('Failed to initialize Peaks instance: ' + err.message);
              if (window.confirm("일시적인 오류가 발생하였습니다. 다시 리로드 하시겠습니까?")){
                window.location.reload();
              }
              return;
            }

            segmentsClick(peaksInstance)
            onPlay(peaksInstance, reactPlayerGlobal)
            onPause(peaksInstance, reactPlayerGlobal)
            onSeek(peaksInstance, reactPlayerGlobal)
            onTimeUpdate(peaksInstance)
            segmentsDragEnd(peaksInstance, reactPlayerGlobal)
            onMouseLeave()
            onSegmentClick(peaksInstance, currentClickedSegmentIdRef)
            addEventToPeaksInstance(peaksInstance)
            setPeaksInstance(peaksInstance)
            peaksInstanceRef.current = peaksInstance ;
            (globalThis as any).peaksInstance = peaksInstance
        });

    }, [mp3Url, captionList]);

    const zoomIn = () => { peaksInstance?.zoom?.zoomIn()};
    const zoomOut = () => {peaksInstance?.zoom?.zoomOut();};

    globalThis.window.document.querySelector('.Peaks')?.addEventListener("click", (e) => {

        const currentTime = performance.now();
        const _600MILLISECOND = 600
        if( globalThis.latestClickedTime != null
          && (currentTime - globalThis.latestClickedTime) < _600MILLISECOND
          && (currentTime - globalThis.latestClickedTime) > 100
          && globalThis.previousSegment != null
          && peaksInstance != null
        ) {

          const currentPeaksTime = peaksInstance.player.getCurrentTime()

          if(globalThis.previousSegment.startTime <= currentPeaksTime
            && currentPeaksTime <= globalThis.previousSegment.endTime
            && reactPlayerGlobal?.current?.props.playing === false
            ) {
              peaksInstance.player.seek(globalThis.previousSegment.startTime)
            }
        }
    });

    function onPlay(peaksInstance: PeaksInstance, reactPlayerGlobal: React.RefObject<ReactPlayer> | null) {

      peaksInstance?.on("player.playing", (obj) => {            
            if (reactPlayerGlobal == null
              || reactPlayerGlobal.current == null)
              return;
            setPlayerState(prev => ({ ...prev, playOn: true }));
          });
    }

    function onPause(peaksInstance: PeaksInstance, reactPlayerGlobal: React.RefObject<ReactPlayer> | null) {
        peaksInstance?.on("player.pause", (obj) => {
          peaksInstance.views.getView('zoomview')?.enableSegmentDragging(true);
          if(  reactPlayerGlobal == null
            || reactPlayerGlobal.current == null
            ) return ;
            setPlayerState(prev => ({...prev, playOn : false}))
        } )
    }

    function onSeek(peaksInstance: PeaksInstance, reactPlayerGlobal: React.RefObject<ReactPlayer> | null) {

          const changeSeekTime = (currentAudioTime : number) => {

            globalThis.currentTime = currentAudioTime
            globalThis.latestSeekedTime = performance.now()


              if(currentAudioTime < 1 &&  currentAudioTime !== 0) {
                // 1초 미만 탐색시 seekTo type을 "fraction"으로 넘김
                // https://github.com/cookpete/react-player#instance-methods

                let setAudioTime = currentAudioTime / 100;

                reactPlayerGlobal?.current?.seekTo(setAudioTime,"fraction") ;
                peaksInstance.player.seek(currentAudioTime)


              } else {
                reactPlayerGlobal?.current?.seekTo(currentAudioTime) ;
              }
        }

        const eventSource = fromEvent(peaksInstance, 'player.seeked');
        const playerSeekedEvent = eventSource.pipe(throttleTime(500))

        playerSeekedEvent.subscribe(changeSeekTime)
    }

    const activateCaptionIfIn = (currentTime : number, eventType : "player.timeupdate"  | "segments.click" ) => {
        
        const peaksInstance = globalThis.peaksInstance
        if(!peaksInstance) return                    
        
        setCaptionList(prevCaptionList => {

            const findCurrentSegment = (segment : CaptionListProps) => Number(segment.startTime) <= currentTime && Number(segment.endTime) >= currentTime
            const currentSegment = prevCaptionList.find(findCurrentSegment)

            if(currentSegment) {

              if(eventType === "segments.click"
              || (eventType === "player.timeupdate" && globalThis.isAutoCaptionHighlight)
              ) {
                setCurrentActivatedSegment(currentSegment)
              }      

              if(eventType === "player.timeupdate" && ! globalThis.isAutoCaptionHighlight) return prevCaptionList


              console.log('currentActivatedSegmentRef.current', currentActivatedSegmentRef.current);
              
              if(currentActivatedSegmentRef.current &&
                currentActivatedSegmentRef.current?.id !== currentSegment.id) {
                const curerntActivatedSegment = peaksInstance.segments.getSegment(currentActivatedSegmentRef.current!.id! as string)
                if(curerntActivatedSegment) {
                  curerntActivatedSegment.update({color : INACTIVE_COLOR})
                  const customEvent = new CustomEvent('customRemove', { detail: { segmentId: curerntActivatedSegment.id , type : "mouseclick" } });
                  document.dispatchEvent(customEvent);
                }
              }

              currentActivatedSegmentRef.current = currentSegment



              const peaksSegment = peaksInstance.segments.getSegment(currentSegment.id!)
              

              peaksSegment?.update({color : BLUE_COLOR})

              const customEvent = new CustomEvent('custom', { detail: { segmentId: currentSegment.id , type : "mouseclick" } });
              document.dispatchEvent(customEvent);

              // deactivate prev segment
              if(currentClickedSegmentIdRef.current
                && currentClickedSegmentIdRef.current !== currentSegment.id) {
                    const prevSegment = peaksInstance.segments.getSegment(currentClickedSegmentIdRef.current)
                    if(prevSegment) {
                      prevSegment.update({color : INACTIVE_COLOR})
                      const customEvent = new CustomEvent('customRemove', { detail: { segmentId: prevSegment.id , type : "mouseclick" } });
                      document.dispatchEvent(customEvent);
                    }
              }
              // currentActivatedSegmentRef.current = currentSegment
              currentClickedSegmentIdRef.current = currentSegment.id

              const nextSegment = findNextSegment({peaksInstance, currentSegment})
              if(nextSegment) {
                nextAfterActivatedSegmentRef.current = nextSegment
              } else {
                // nextAfterActivatedSegmentRef.current = null 
                // alert("다음 세그먼트가 없습니다")
              }
              // nextAfterActivatedSegmentRef.current              
            }

            return prevCaptionList
        })
    }

    function onTimeUpdate(peaksInstance: PeaksInstance) {
            peaksInstance?.on("player.timeupdate", (currentTime : number) => activateCaptionIfIn(currentTime, "player.timeupdate") )
    }

    function onMouseLeave() {
        peaksInstance?.on("segments.mouseleave", (obj) => {
            const customEvent = new CustomEvent('customRemove', { detail: { segmentId: obj.segment.id } });
            document.dispatchEvent(customEvent);
        } )
    }

    function onSegmentClick(peaksInstance :PeaksInstance, currentClickedSegmentIdRef: React.MutableRefObject<string | undefined>) {

          peaksInstance.on("segments.click", function changeSegmentColor(segmentEvt) {           
              setTimeout(() => {
                  activateCaptionIfIn(peaksInstance.player.getCurrentTime(), "segments.click")              
              }, 0);
          })
    }


    function segmentsClick(peaksInstance: PeaksInstance) {


          peaksInstance.on('segments.click', function(segmentEvt) {
            
              const currentTime = performance.now()
              globalThis.latestClickedTime = currentTime ;
              globalThis.previousSegment = segmentEvt.segment ;
              latestSegmentId.current = segmentEvt.segment.id!
              latestClickedTime.current = currentTime ;
          });
    }

    function updateSegmentTime({obj , peaksInstance} :{obj : SegmentDragEvent, peaksInstance: PeaksInstance}) {
          const segments = peaksInstance.segments.getSegments()
          const foundSeg = segments.find(segment => String(segment.id).trim() === String(obj.segment.id).trim())
          if(foundSeg != null) {
              const [_1stLine, _2ndLine] = obj.segment.labelText?.split('\n\n')!
              const [segmentSequenceStr, ] = _1stLine.split(' ')!

              foundSeg.update({labelText : `#${Number(segmentSequenceStr.replace(/[^0-9]/g, ""))} ${(obj.segment.endTime - obj.segment.startTime).toFixed(1)}s \n\n${_2ndLine}`})

          } else {
            alert('peaksInstance에서 id 못찾음')
          }
    }



    function segmentsDragEnd(peaksInstance: PeaksInstance, reactPlayerAtomRef : React.RefObject<ReactPlayer> | null) {

       function isNextSegmentTooClose(prevCaptionList : CaptionListProps[], segmentEvt : SegmentDragEvent) :  {isNextTooClose : boolean, nextStartTime : number}{
            const currentSegmentIndex = prevCaptionList.findIndex(caption => caption.id === segmentEvt.segment.id)
            
            const currentSegment = segmentEvt.segment
            const previousSegment = prevCaptionList.at(currentSegmentIndex -1)
            const nextSegment = prevCaptionList.at(currentSegmentIndex +1)

            if((currentSegment && nextSegment)
            && MINIMUM_CAPTION_GAP >= (nextSegment.startTime as number) - (currentSegment.endTime as number)
            ) {
              return {isNextTooClose: true, nextStartTime : nextSegment!.startTime as number}
            }
            return {isNextTooClose : false, nextStartTime : -1}
       }

        function isPrevSegmentTooClose(prevCaptionList : CaptionListProps[], segmentEvt : SegmentDragEvent) : {isPrevTooClose : boolean, previousSegEndTime : number}{
            const currentSegmentIndex = prevCaptionList.findIndex(caption => caption.id === segmentEvt.segment.id)
            const currentSegment = segmentEvt.segment
            const previousSegment = prevCaptionList.at(currentSegmentIndex -1)
            const nextSegment = prevCaptionList.at(currentSegmentIndex +1)

            if((currentSegment && previousSegment)
              && MINIMUM_CAPTION_GAP >= (currentSegment.startTime as number) - (previousSegment.endTime as number)
              ) {
                
                return {isPrevTooClose : true, previousSegEndTime : previousSegment.endTime as number}
            }
            return {isPrevTooClose : false, previousSegEndTime : -1}
        }

        peaksInstance?.on("segments.dragged", (segmentEvt) => {

            // STALE CLOSURE 회피
            setCaptionList(prevCaptionList => {
              
                const isSideDrag =  segmentEvt.marker
                const isStartMarkerDrag = segmentEvt.startMarker
                const segmentDuration = segmentEvt.segment.endTime - segmentEvt.segment. startTime
                const {isPrevTooClose, previousSegEndTime: previousSegEndTime} = isPrevSegmentTooClose(prevCaptionList, segmentEvt)
                
                if((isSideDrag && isStartMarkerDrag && isPrevTooClose)
                || (!isSideDrag && isPrevTooClose)) {

                  segmentEvt.segment.update({startTime : previousSegEndTime + MINIMUM_CAPTION_GAP, 
                                            endTime : isSideDrag
                                                      ? segmentEvt.segment.endTime 
                                                      : previousSegEndTime + MINIMUM_CAPTION_GAP + segmentDuration})
                  
                  return prevCaptionList;
                }

                const {isNextTooClose, nextStartTime} = isNextSegmentTooClose(prevCaptionList, segmentEvt)

                if(isNextTooClose) {
                    segmentEvt.segment.update({startTime : isSideDrag 
                                                ? segmentEvt.segment.startTime
                                                : nextStartTime - MINIMUM_CAPTION_GAP - segmentDuration,
                                                endTime : nextStartTime - MINIMUM_CAPTION_GAP});

                    return prevCaptionList;
                }

                const customEvent = new CustomEvent('custom', { detail: { segmentId: segmentEvt.segment.id,  isPrevTooClose, isNextTooClose} });
                document.dispatchEvent(customEvent);

                return prevCaptionList
            })
        })

        peaksInstance?.on("segments.mouseenter", (segmentEvt : SegmentMouseEvent) => {

              const segment : Segment = segmentEvt.segment

              if(currentClickedSegmentIdRef.current !== segment.id
                && segment.id !== currentHoverSegmentIdRef.current) {
                  segment.update({color : ORANGE_COLOR})
              }

              if(  currentHoverSegmentIdRef.current != null
                && currentHoverSegmentIdRef.current !== segment.id
                && peaksInstance
                ) {

                    previouslyHoverSegmentIdRef.current = currentHoverSegmentIdRef.current
                    if(previouslyHoverSegmentIdRef.current === currentClickedSegmentIdRef.current) {

                    } else {

                        const forDeActiveSegment = peaksInstance.segments.getSegment(currentHoverSegmentIdRef.current)
                        if(forDeActiveSegment) {
                          forDeActiveSegment.update({color : INACTIVE_COLOR})
                          const customEvent = new CustomEvent('customRemove', {detail: { segmentId: forDeActiveSegment.id , type : "mouseclick" } });
                          document.dispatchEvent(customEvent);

                        }
                    }
              }
              currentHoverSegmentIdRef.current = segment.id

              const customEvent = new CustomEvent('custom', { detail: { segmentId: segment.id , type : "mouseenter" } });
              document.dispatchEvent(customEvent);
      })

      peaksInstance?.on("segments.dragend", (segmentEvt) => {

        setCaptionList(prevSegments => {

              const cloneSegments = structuredClone(prevSegments)
              const matchedIdx = prevSegments.findIndex(segment => {
                if(segment.id!.trim() === segmentEvt.segment.id!.trim()) {
                  return true
                } else return false
              })

              if(matchedIdx >= 0 ) {
                cloneSegments[matchedIdx].startTime = segmentEvt.segment.startTime ;
                cloneSegments[matchedIdx].endTime = segmentEvt.segment.endTime ;
                cloneSegments[matchedIdx].time_ini = secondsToFormattedTime(segmentEvt.segment.startTime)
                cloneSegments[matchedIdx].time_end = secondsToFormattedTime(segmentEvt.segment.endTime)
              } else {
                alert('segment id 못찾음')
              }
              return cloneSegments
        })

        if(peaksInstance) {
              updateSegmentTime({obj: segmentEvt , peaksInstance})
        } else {
          alert('peaksInstance가 null')
        }
    })
    }

      function addEventToPeaksInstance(peaksInstance : PeaksInstance) {
          const view = peaksInstance.views.getView('zoomview')!
          view.enableSegmentDragging(true)
          view.setSegmentDragMode('no-overlap');
      }

      return {
        nextAfterActivatedSegmentRef,
        currentActivatedSegmentRef,
        zoomIn,
        zoomOut,
        zoomviewWaveformRef,
        audioElementRef ,
        segments, 
        setSegments,
        peaksInstance, 
        setPeaksInstance, 
        mp3Url, 
        captionArr: captionList, 
        setCaptionArr: setCaptionList
      }
}
